import { ReactElement, RefObject, useLayoutEffect, useRef } from 'react';
import { Grid, GridHandle } from './Grid';
import { gridContainer } from './styles.css';
import { Box, ModalContainer } from '@ff-it/ui';
import { PlanProvider, usePlanMeta, usePlanPresence } from './state';
import { useEntity } from 'components';
import { BlockDetails } from '../block/types';
import { Provider as JotaiProvider } from 'jotai';
import { Toolbar } from './Toolbar';
import { useQuery } from 'hooks';
import { PLAN_MODAL_SCOPE } from './openPlanModal';

// this is nested component since grid might be suspending
function RestoreSelection({ gridRef }: { gridRef: RefObject<GridHandle> }): null {
  const query = useQuery();
  const restored = useRef<string>();
  // biome-ignore lint/correctness/useExhaustiveDependencies: gridRef is fine
  useLayoutEffect(() => {
    const rowId = query.get('rowId');
    if (rowId && rowId !== restored.current) {
      gridRef.current?.selectCell(`row-${rowId}`, 'title');
      restored.current = rowId;
    }
  }, [query]);
  return null;
}

function PlanContainer({ gridRef }: { gridRef: RefObject<GridHandle> }): ReactElement {
  const { id, planning } = usePlanMeta();
  const { markers, onSelectedCellChange } = usePlanPresence();

  return (
    <>
      <ModalContainer scope={PLAN_MODAL_SCOPE} />
      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        px="1"
        data-testid={`plan-${id}`}
        data-planning={planning}
      >
        <Toolbar />
        <div className={gridContainer}>
          <Grid ref={gridRef} onSelectedCellChange={onSelectedCellChange} presenceMarkers={markers} />
          <RestoreSelection gridRef={gridRef} />
        </div>
      </Box>
    </>
  );
}

export function Plan(): ReactElement {
  const gridRef = useRef<GridHandle>(null);
  const {
    item: { id, _revision },
    mutate,
  } = useEntity<BlockDetails>();
  return (
    <JotaiProvider key={id}>
      <PlanProvider gridRef={gridRef} _revision={_revision} mutate={mutate}>
        <PlanContainer gridRef={gridRef} />
      </PlanProvider>
    </JotaiProvider>
  );
}
